import React, { useEffect, useRef, useState } from 'react'
import Style from './download-menu.module.scss'

interface DownloadUrls {
    mac: string
    windows: string
}

interface AiDownloadMenuProps {
    downloads: DownloadUrls
    placement?: 'above' | 'below'
    className?: string
    triggerClassName?: string
    children: React.ReactNode
}

type CurrentVersion = 'mac' | 'windows' | null

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ')
const MAC_ICON_PATH = [
    'M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.3.03-1.72-.79-3.2-.79-1.49 0-1.95.77-3.18.82',
    '-1.31.05-2.3-1.32-3.14-2.53-1.71-2.48-3.05-7-1.28-10.06.87-1.52 2.46-2.48 4.17-2.51',
    ' 1.3-.03 2.53.87 3.2.87.64 0 2.14-1.08 3.61-.92.62.03 2.35.25 3.46 1.88-.09.06-2.07 1.22-2.05 3.63',
    '.03 2.88 2.52 3.84 2.55 3.85-.02.07-.4 1.37-1.31 2.79M14.1 4.98c.71-.86 1.2-2.06 1.07-3.25',
    '-1.03.04-2.28.69-3.02 1.55-.66.76-1.24 1.98-1.09 3.14 1.15.09 2.33-.58 3.04-1.44Z',
].join('')

const detectCurrentVersion = (): CurrentVersion => {
    if (typeof navigator === 'undefined') return null

    const platform = String(navigator.platform || '').toLowerCase()
    const userAgent = navigator.userAgent.toLowerCase()
    const isIPadOS =
        userAgent.includes('ipad') ||
        userAgent.includes('iphone') ||
        userAgent.includes('ipod') ||
        ((platform.includes('mac') || userAgent.includes('macintosh')) && navigator.maxTouchPoints > 1)

    if (isIPadOS) return null
    if (platform.includes('mac') || userAgent.includes('macintosh')) return 'mac'
    if (platform.includes('win') || userAgent.includes('windows')) return 'windows'

    return null
}

const PlatformIcon = ({ platform }: { platform: Exclude<CurrentVersion, null> }) => {
    if (platform === 'mac') {
        return (
            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <path d={MAC_ICON_PATH} />
            </svg>
        )
    }

    return (
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path d="M2.5 2.5h8.25v8.25H2.5V2.5Zm10.75 0h8.25v8.25h-8.25V2.5ZM2.5 13.25h8.25v8.25H2.5v-8.25Zm10.75 0h8.25v8.25h-8.25v-8.25Z" />
        </svg>
    )
}

export default function AiDownloadMenu({
    downloads,
    placement = 'below',
    className,
    triggerClassName,
    children,
}: AiDownloadMenuProps) {
    const detailsRef = useRef<HTMLDetailsElement>(null)
    const [currentVersion, setCurrentVersion] = useState<CurrentVersion>(null)

    useEffect(() => {
        setCurrentVersion(detectCurrentVersion())

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (detailsRef.current && detailsRef.current.open && !detailsRef.current.contains(event.target as Node)) {
                detailsRef.current.removeAttribute('open')
            }
        }
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && detailsRef.current && detailsRef.current.open) {
                detailsRef.current.removeAttribute('open')
                const trigger = detailsRef.current.querySelector<HTMLElement>('summary')
                if (trigger) trigger.focus()
            }
        }

        document.addEventListener('click', closeOnOutsideClick)
        document.addEventListener('keydown', closeOnEscape)

        return () => {
            document.removeEventListener('click', closeOnOutsideClick)
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [])

    const downloadVersion = (url: string) => {
        if (detailsRef.current) detailsRef.current.removeAttribute('open')
        const link = document.createElement('a')
        link.href = url
        link.referrerPolicy = 'no-referrer'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        link.remove()
    }

    return (
        <details ref={detailsRef} className={cx(Style.root, className)}>
            <summary className={triggerClassName}>{children}</summary>
            <div className={cx(Style.menu, placement === 'above' && Style.above)} aria-label="选择电脑版本">
                <button type="button" className={Style.item} onClick={() => downloadVersion(downloads.mac)}>
                    <span className={Style.icon}>
                        <PlatformIcon platform="mac" />
                    </span>
                    <strong>Mac 通用版</strong>
                    {currentVersion === 'mac' && <span className={Style.current}>当前设备</span>}
                </button>
                <button type="button" className={Style.item} onClick={() => downloadVersion(downloads.windows)}>
                    <span className={Style.icon}>
                        <PlatformIcon platform="windows" />
                    </span>
                    <strong>Windows 10/11 版</strong>
                    {currentVersion === 'windows' && <span className={Style.current}>当前设备</span>}
                </button>
            </div>
        </details>
    )
}
